import { useState, useEffect } from "react";
import {
  X,
  Loader2,
  Mail,
  AlertTriangle,
  Send,
  Users,
} from "lucide-react";
import { editorColors as colors, fonts, CONTACT_TYPES } from "../../styles/theme";
import { sendNewsletter, fetchDelegateConfig } from "../../services/api";
import { getGraphToken } from "../../services/msal";
import { SPAM_TRIGGER_WORDS } from "../../extensions/SpamHighlight";

// ── Pre-send validation ────────────────────────────────────────
const SPAM_WORDS = SPAM_TRIGGER_WORDS;

function validateNewsletter({
  subject,
  preheader,
  bodyHtml,
  segment,
  customEmails,
  segmentCounts,
}) {
  const warnings = [];

  // Subject checks
  if (!subject.trim()) {
    warnings.push({ severity: "error", message: "Subject line is empty" });
  } else {
    if (subject.length > 60) {
      warnings.push({
        severity: "warn",
        message: `Subject is ${subject.length} chars — may be truncated in mobile inboxes (60 char limit)`,
      });
    }
    if (subject === subject.toUpperCase() && subject.length > 5) {
      warnings.push({
        severity: "warn",
        message: "ALL CAPS subject lines trigger spam filters",
      });
    }
    if (/[!]{2,}/.test(subject) || /[?!]{3,}/.test(subject)) {
      warnings.push({
        severity: "warn",
        message: "Excessive punctuation in subject triggers spam filters",
      });
    }
    const subjectLower = subject.toLowerCase();
    const found = SPAM_WORDS.filter((w) => subjectLower.includes(w));
    if (found.length > 0) {
      warnings.push({
        severity: "warn",
        message: `Subject contains spam trigger words: ${found.join(", ")}`,
      });
    }
  }

  // Preheader check
  if (!preheader?.trim()) {
    warnings.push({
      severity: "info",
      message: "No preview text — inbox will show first line of body instead",
    });
  }

  // Body checks
  if (!bodyHtml || bodyHtml.length < 50) {
    warnings.push({
      severity: "error",
      message: "Newsletter body is too short or empty",
    });
  }

  // Body spam word check
  if (bodyHtml) {
    const bodyText = bodyHtml.replace(/<[^>]+>/g, " ").toLowerCase();
    const bodySpam = SPAM_WORDS.filter((w) => bodyText.includes(w));
    if (bodySpam.length > 0) {
      warnings.push({
        severity: "warn",
        message: `Body contains spam trigger words (highlighted in editor): ${bodySpam.join(", ")}`,
      });
    }
  }

  // Gmail clipping: estimate compiled size (~2x raw body for MJML wrapper)
  const estimatedSize = (bodyHtml?.length || 0) * 2;
  if (estimatedSize > 90000) {
    warnings.push({
      severity: "warn",
      message:
        "Email is very large — Gmail will clip messages over 102KB. Consider shortening.",
    });
  }

  // Image checks
  const imgCount = (bodyHtml?.match(/<img /gi) || []).length;
  if (imgCount > 8) {
    warnings.push({
      severity: "warn",
      message: `${imgCount} images — heavy image emails load slowly and may trigger spam filters`,
    });
  }

  // Recipient count
  const count = segment === "custom"
    ? (customEmails || []).length
    : Number(String(segmentCounts[segment] || "0").replace(/,/g, ""));
  if (count === 0) {
    warnings.push({
      severity: "error",
      message: segment === "custom" ? "No custom email addresses added" : "No recipients in selected segment",
    });
  }

  return warnings;
}

export default function SendConfirmModal({
  draftId,
  subject,
  preheader,
  bodyHtml,
  segment,
  customEmails = [],
  segmentCounts,
  audienceLists = [],
  tenant,
  onClose,
  onSent,
}) {
  const [sendMethod, setSendMethod] = useState("sendgrid");
  const [senderEmail, setSenderEmail] = useState("");
  const [delegates, setDelegates] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const warnings = validateNewsletter({
    subject,
    preheader,
    bodyHtml,
    segment,
    customEmails,
    segmentCounts,
  });
  const hasErrors = warnings.some((w) => w.severity === "error");

  useEffect(() => {
    if (tenant.features?.o365) {
      fetchDelegateConfig()
        .then((cfg) => {
          const emails = cfg?.delegates?.flatMap((d) => d.canSendAs) || [];
          setDelegates([...new Set(emails)]);
        })
        .catch(() => {});
    }
  }, [tenant]);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      let graphToken = null;
      if (sendMethod === "o365" || sendMethod === "draft") {
        graphToken = await getGraphToken();
      }
      const res = await sendNewsletter(draftId, {
        sendMethod,
        senderEmail: sendMethod !== "sendgrid" ? senderEmail : undefined,
        graphToken,
      });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const segmentLabel = {
    test: "Test Group",
    all: "All Contacts",
    custom: `Custom (${customEmails.length} emails)`,
    ...Object.fromEntries(
      Object.entries(CONTACT_TYPES).map(([id, info]) => [id, info.label]),
    ),
    digest: "Monthly Digest",
    ...Object.fromEntries(
      audienceLists.map((l) => [`list:${l.id}`, l.name]),
    ),
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !sending) {
          result ? onSent() : onClose();
        }
      }}
    >
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          width: 440,
          maxWidth: "90vw",
          padding: 24,
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 18,
              fontWeight: 700,
              color: colors.text,
              margin: 0,
            }}
          >
            Send Newsletter
          </h3>
          {!sending && (
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: colors.textSecondary,
                cursor: "pointer",
              }}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {result ? (
          /* Results view */
          <div>
            <div
              style={{
                padding: 16,
                background: colors.bg,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: colors.text,
                  marginBottom: 12,
                }}
              >
                Send Complete
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 12, color: colors.textSecondary }}>
                  Total:{" "}
                  <strong style={{ color: colors.text }}>{result.total}</strong>
                </div>
                <div style={{ fontSize: 12, color: colors.success }}>
                  Sent: <strong>{result.sent}</strong>
                </div>
                <div style={{ fontSize: 12, color: colors.danger }}>
                  Failed: <strong>{result.failed}</strong>
                </div>
                <div style={{ fontSize: 12, color: colors.textSecondary }}>
                  Skipped: <strong>{result.skipped}</strong>
                </div>
              </div>
              {result.errors && result.errors.length > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    padding: 10,
                    background: "rgba(239,68,68,0.06)",
                    borderRadius: 6,
                    border: "1px solid rgba(239,68,68,0.15)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: colors.danger,
                      marginBottom: 6,
                    }}
                  >
                    Error details:
                  </div>
                  <div
                    style={{
                      maxHeight: 120,
                      overflowY: "auto",
                      fontSize: 11,
                      color: colors.textSecondary,
                      lineHeight: 1.6,
                    }}
                  >
                    {result.errors.map((e, i) => (
                      <div
                        key={i}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {e.email}: {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={onSent}
              style={{
                width: "100%",
                padding: "10px 16px",
                borderRadius: 6,
                border: "none",
                background: colors.accent,
                color: colors.bg,
                fontSize: 13,
                fontWeight: 700,
                fontFamily: fonts.body,
                cursor: "pointer",
              }}
            >
              Back to Newsletters
            </button>
          </div>
        ) : (
          /* Configuration view */
          <div>
            {/* Summary */}
            <div
              style={{
                padding: 16,
                background: colors.bg,
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <Mail size={14} color={colors.accent} />
                <span
                  style={{ fontSize: 13, fontWeight: 600, color: colors.text }}
                >
                  {subject || "(no subject)"}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Users size={14} color={colors.textSecondary} />
                <span style={{ fontSize: 12, color: colors.textSecondary }}>
                  {segmentLabel[segment] || segment} —{" "}
                  {segment === "custom" ? customEmails.length : (segmentCounts[segment] || "?")} recipients
                </span>
              </div>
            </div>

            {/* Send Method */}
            <div style={{ marginBottom: 16 }}>
              <label
                style={{
                  fontSize: 10,
                  color: colors.textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 700,
                  display: "block",
                  marginBottom: 8,
                }}
              >
                Send Method
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  {
                    id: "sendgrid",
                    label: "SendGrid",
                    desc: "Bulk email service",
                  },
                  ...(tenant.features?.o365
                    ? [
                        { id: "o365", label: "O365", desc: "Send via Outlook" },
                        {
                          id: "draft",
                          label: "Draft",
                          desc: "Create Outlook drafts",
                        },
                      ]
                    : []),
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSendMethod(m.id)}
                    style={{
                      flex: 1,
                      padding: "10px 8px",
                      borderRadius: 6,
                      border: `1px solid ${sendMethod === m.id ? colors.accent : colors.border}`,
                      background:
                        sendMethod === m.id ? colors.accentDim : colors.bg,
                      color:
                        sendMethod === m.id
                          ? colors.accent
                          : colors.textSecondary,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600 }}>
                      {m.label}
                    </div>
                    <div style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>
                      {m.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sender email for O365/Draft */}
            {(sendMethod === "o365" || sendMethod === "draft") && (
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    fontSize: 10,
                    color: colors.textSecondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 700,
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Send As
                </label>
                <select
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    background: colors.bg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    color: colors.text,
                    fontSize: 13,
                    fontFamily: fonts.body,
                  }}
                >
                  <option value="">Your email (default)</option>
                  {delegates.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Pre-send validation */}
            {warnings.length > 0 && (
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {warnings.map((w, i) => {
                  const color =
                    w.severity === "error"
                      ? colors.danger
                      : w.severity === "warn"
                        ? colors.warning
                        : colors.textSecondary;
                  return (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        padding: "8px 12px",
                        background: `${color}12`,
                        borderRadius: 6,
                        border: `1px solid ${color}22`,
                      }}
                    >
                      <AlertTriangle
                        size={12}
                        color={color}
                        style={{ flexShrink: 0, marginTop: 1 }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          color: colors.textSecondary,
                          lineHeight: 1.4,
                        }}
                      >
                        {w.message}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recipient info */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: 12,
                background: `${colors.warning}12`,
                borderRadius: 6,
                marginBottom: 16,
              }}
            >
              <AlertTriangle
                size={14}
                color={colors.warning}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  lineHeight: 1.4,
                }}
              >
                {segment === "custom"
                  ? `This will send to ${customEmails.length} custom email address${customEmails.length !== 1 ? "es" : ""}: ${customEmails.join(", ")}.`
                  : `This will send to ${segmentCounts[segment] || "?"} recipients in the ${segmentLabel[segment]?.toLowerCase() || segment} segment. Unsubscribed contacts will be skipped.`}
              </span>
            </div>

            {error && (
              <div
                style={{
                  padding: 10,
                  background: `${colors.danger}18`,
                  borderRadius: 6,
                  marginBottom: 16,
                  fontSize: 12,
                  color: colors.danger,
                }}
              >
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={onClose}
                disabled={sending}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  borderRadius: 6,
                  border: `1px solid ${colors.border}`,
                  background: colors.bgCard,
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                  cursor: sending ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending || hasErrors}
                title={hasErrors ? "Fix errors above before sending" : ""}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  padding: "10px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: colors.accent,
                  color: colors.bg,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: fonts.body,
                  cursor: sending || hasErrors ? "not-allowed" : "pointer",
                  opacity: sending || hasErrors ? 0.5 : 1,
                }}
              >
                {sending ? (
                  <>
                    <Loader2
                      size={14}
                      className="spin"
                    />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Send Now
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
